import { useState, useEffect } from 'react'
import { useClerk, useUser } from '@clerk/clerk-react'
import { useDarkMode, toggleTheme } from '../outline/theme.js'
import { hasModule, isSeatLoaded } from '../services/tenancy.js'
import { ctx } from '../outline/ctx.js'
import { openFinances } from './FinancesTabs.jsx'
import { isEmbedded } from '../outline/state.js'
import { getLineUpShort, onLineUpShort } from '../services/onDeck.js'

/* ── Icon helpers ───────────────────────────────────────────────────────── */

function IconGrid() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
      <rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
      <rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
      <rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor" opacity="0.9"/>
    </svg>
  )
}

function IconClose() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <line x1="3" y1="3" x2="13" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="13" y1="3" x2="3" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  )
}

function IconSettings() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="2.4" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M8 1.5v1.6M8 12.9v1.6M14.5 8h-1.6M3.1 8H1.5M12.4 3.6l-1.1 1.1M4.7 11.3l-1.1 1.1M12.4 12.4l-1.1-1.1M4.7 4.7 3.6 3.6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  )
}

function IconSignOut() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M6 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <polyline points="11,5 14,8 11,11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="14" y1="8" x2="6" y2="8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  )
}

function IconSun() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.4"/>
      <line x1="8" y1="1" x2="8" y2="3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="8" y1="13" x2="8" y2="15" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="1" y1="8" x2="3" y2="8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="13" y1="8" x2="15" y2="8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="3.05" y1="3.05" x2="4.46" y2="4.46" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="11.54" y1="11.54" x2="12.95" y2="12.95" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="12.95" y1="3.05" x2="11.54" y2="4.46" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
      <line x1="4.46" y1="11.54" x2="3.05" y2="12.95" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  )
}

function IconMoon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M13.5 10.5A6 6 0 0 1 5.5 2.5a6.001 6.001 0 1 0 8 8z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

/* ── Helpers ────────────────────────────────────────────────────────────── */

function userInitials(user) {
  const name = user?.fullName || user?.firstName || ''
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
  return '?'
}

// Build a URL for a workspace, clearing all other workspace params first.
// Pass {} for the Projects workspace (no ws param), {ws:'tracker'}, or {ws:'billing',wsTool:'readiness'}.
function wsUrl(params) {
  const url = new URL(window.location)
  ;['ws', 'wsTool', 'wsRunId', 'wsConceptId', 'wsMsId', 'wsMsName', 'wsMsType', 'wsProjectName'].forEach(k => url.searchParams.delete(k))
  Object.entries(params).forEach(([k, v]) => v != null && url.searchParams.set(k, v))
  return url.toString()
}

/* ── Main component ─────────────────────────────────────────────────────── */

const NAV_W = 228
const NAV_OPEN_KEY = 'aj:nav:open'

export default function SiteNav() {
  // Persist open/closed across refresh — initialise from localStorage. A peek/split iframe
  // (?split=1) always starts closed and ignores the stored value entirely — Matt: "it also
  // sometimes opens with the left menu expanded... don't show the left menu" — same-origin
  // storage is shared with the parent tab, so without this the nav's open/closed state would
  // leak in from whatever the parent tab was last left as.
  const [open, setOpen] = useState(() => {
    if (isEmbedded()) return false
    try { return localStorage.getItem(NAV_OPEN_KEY) === 'true' } catch { return false }
  })
  // Re-render when tenancy seat data arrives (the seat cache starts empty)
  const [, setSeatTick] = useState(0)
  const { signOut }     = useClerk()
  const { user }        = useUser()
  const dark            = useDarkMode()
  const isAdmin         = isSeatLoaded() && hasModule('admin')
  const isTimeReviewer  = isSeatLoaded() && hasModule('timecard-admin')
  const canSpecLibrary  = isSeatLoaded() && (hasModule('spec-library') || hasModule('admin'))
  const canScheduleLibrary = isSeatLoaded() && (hasModule('spec-library') || hasModule('admin'))
  const canTemplates    = isSeatLoaded() && (hasModule('checklist-templates') || hasModule('admin'))
  const canBilling      = isSeatLoaded() && hasModule('billing-admin')
  // Performance / allocation surfaces (Team Workload, Schedule Pulse) — manager-and-up only.
  const canWorkload     = isSeatLoaded() && (hasModule('workload') || hasModule('admin'))
  // The Flight Board's red count: the line-up is short when fewer milestones are ready to start than
  // people who need new work this week (LineUpTab.jsx; replaced the running-dry lockout, 2026-09-27).
  // Tower seats only, never in an embedded copy; re-read every 15 minutes and whenever the line-up changes.
  const [lineUpShort, setLineUpShort] = useState(null)
  useEffect(() => {
    if (!canWorkload || isEmbedded()) return undefined
    const off = onLineUpShort(setLineUpShort)
    const read = () => getLineUpShort().then(setLineUpShort).catch(() => {})
    read()
    const t = setInterval(read, 15 * 60 * 1000)
    return () => { off(); clearInterval(t) }
  }, [canWorkload])
  // CRM workspace (Leads / Opportunities / Proposals + Fee Models) — crm seat or admin.
  const canCrm          = isSeatLoaded() && (hasModule('crm') || hasModule('admin'))
  // Roadmap & Bugs workspace — manager and up only.
  const canRoadmap      = isSeatLoaded() && (hasModule('roadmap') || hasModule('admin'))
  // Permitting workspace (Zones & Jurisdictions library + cross-project Permits board) —
  // firm-standard reference data, manager and up only (mirrors checklist-templates' rationale).
  const canPermitting   = isSeatLoaded() && (hasModule('permitting') || hasModule('admin'))
  // Bookkeeping / Practice Finance — payroll, owner distributions, individual pay rates.
  // `finance-admin` and NOTHING else. Falling back to 'admin' here put the button in front of
  // every admin, which is exactly what database/249 took away when it moved this module off the
  // admin seat onto named people. The module is the whole point — widening it is a grant in
  // Admin → Users, never a code change (PRACTICE_FINANCE_PLAN §3.1).
  const canFinance      = isSeatLoaded() && hasModule('finance-admin')

  // Which workspace is active (so the nav highlights it). Billing tracks the BillingWorkspace
  // open/close events + its persisted state, so a refresh shows the right item selected.
  const [billingActive, setBillingActive] = useState(() => {
    // Per-tab first (sessionStorage), then the shared snapshot — matches BillingWorkspace.
    try {
      const raw = sessionStorage.getItem('aj:billing:ws') || localStorage.getItem('aj:billing:ws') || 'null'
      const s = JSON.parse(raw); return !!(s && s.active)
    } catch { return false }
  })
  const [trackerActive, setTrackerActive] = useState(() => {
    try {
      const raw = sessionStorage.getItem('aj:tracker:ws') || localStorage.getItem('aj:tracker:ws') || 'null'
      const s = JSON.parse(raw); return !!(s && s.active)
    } catch { return false }
  })
  const [pulseActive, setPulseActive] = useState(() => {
    try {
      const raw = sessionStorage.getItem('aj:pulse:ws') || localStorage.getItem('aj:pulse:ws') || 'null'
      const s = JSON.parse(raw); return !!(s && s.active)
    } catch { return false }
  })
  const [teamActive, setTeamActive] = useState(() => {
    try {
      const raw = sessionStorage.getItem('aj:team:ws') || localStorage.getItem('aj:team:ws') || 'null'
      const s = JSON.parse(raw); return !!(s && s.active)
    } catch { return false }
  })
  const [crmActive, setCrmActive] = useState(() => {
    try {
      const raw = sessionStorage.getItem('aj:crm:ws') || localStorage.getItem('aj:crm:ws') || 'null'
      const s = JSON.parse(raw); return !!(s && s.active)
    } catch { return false }
  })
  const [templatesActive, setTemplatesActive] = useState(false)
  const [studioActive, setStudioActive] = useState(() => {
    try {
      const raw = sessionStorage.getItem('aj:studio:ws') || localStorage.getItem('aj:studio:ws') || 'null'
      const s = JSON.parse(raw); return !!(s && s.active)
    } catch { return false }
  })
  const [permittingActive, setPermittingActive] = useState(() => {
    try {
      const raw = sessionStorage.getItem('aj:permitting:ws') || localStorage.getItem('aj:permitting:ws') || 'null'
      const s = JSON.parse(raw); return !!(s && s.active)
    } catch { return false }
  })
  const [constructionActive, setConstructionActive] = useState(() => {
    try {
      const raw = sessionStorage.getItem('aj:construction:ws') || localStorage.getItem('aj:construction:ws') || 'null'
      const s = JSON.parse(raw); return !!(s && s.active)
    } catch { return false }
  })
  const [financeActive, setFinanceActive] = useState(() => {
    try {
      const raw = sessionStorage.getItem('aj:finance:ws') || localStorage.getItem('aj:finance:ws') || 'null'
      const s = JSON.parse(raw); return !!(s && s.active)
    } catch { return false }
  })

  /* Toggle on hamburger click — a no-op in a peek/split iframe, which never shows the nav. */
  useEffect(() => {
    if (isEmbedded()) return
    function onToggle() { setOpen(o => !o) }
    window.addEventListener('toggle-site-nav', onToggle)
    return () => window.removeEventListener('toggle-site-nav', onToggle)
  }, [])

  /* Track which workspace is active so the nav highlight follows it */
  useEffect(() => {
    function onBilling() { setBillingActive(true) }
    function offBilling() { setBillingActive(false) }
    function onTracker() { setTrackerActive(true) }
    function offTracker() { setTrackerActive(false) }
    function onPulse() { setPulseActive(true) }
    function offPulse() { setPulseActive(false) }
    function onTeam() { setTeamActive(true) }
    function offTeam() { setTeamActive(false) }
    function onTemplates() { setTemplatesActive(true) }
    function offTemplates() { setTemplatesActive(false) }
    function onCrm() { setCrmActive(true) }
    function offCrm() { setCrmActive(false) }
    function onStudio() { setStudioActive(true) }
    function offStudio() { setStudioActive(false) }
    function onPermitting() { setPermittingActive(true) }
    function offPermitting() { setPermittingActive(false) }
    function onConstruction() { setConstructionActive(true) }
    function offConstruction() { setConstructionActive(false) }
    function onFinance() { setFinanceActive(true) }
    function offFinance() { setFinanceActive(false) }
    window.addEventListener('open-crm', onCrm)
    window.addEventListener('close-crm', offCrm)
    window.addEventListener('open-studio', onStudio)
    window.addEventListener('close-studio', offStudio)
    window.addEventListener('open-permitting', onPermitting)
    window.addEventListener('close-permitting', offPermitting)
    window.addEventListener('open-construction-hub', onConstruction)
    window.addEventListener('close-construction-hub', offConstruction)
    window.addEventListener('open-finance', onFinance)
    window.addEventListener('close-finance', offFinance)
    window.addEventListener('open-billing', onBilling)
    window.addEventListener('close-billing', offBilling)
    window.addEventListener('open-tracker', onTracker)
    window.addEventListener('close-tracker', offTracker)
    window.addEventListener('open-pulse', onPulse)
    window.addEventListener('close-pulse', offPulse)
    window.addEventListener('open-team', onTeam)
    window.addEventListener('close-team', offTeam)
    window.addEventListener('open-templates', onTemplates)
    window.addEventListener('close-templates', offTemplates)
    return () => {
      window.removeEventListener('open-billing', onBilling)
      window.removeEventListener('close-billing', offBilling)
      window.removeEventListener('open-tracker', onTracker)
      window.removeEventListener('close-tracker', offTracker)
      window.removeEventListener('open-pulse', onPulse)
      window.removeEventListener('close-pulse', offPulse)
      window.removeEventListener('open-team', onTeam)
      window.removeEventListener('close-team', offTeam)
      window.removeEventListener('open-templates', onTemplates)
      window.removeEventListener('close-templates', offTemplates)
      window.removeEventListener('open-crm', onCrm)
      window.removeEventListener('close-crm', offCrm)
      window.removeEventListener('open-studio', onStudio)
      window.removeEventListener('close-studio', offStudio)
      window.removeEventListener('open-permitting', onPermitting)
      window.removeEventListener('close-permitting', offPermitting)
      window.removeEventListener('open-construction-hub', onConstruction)
      window.removeEventListener('close-construction-hub', offConstruction)
      window.removeEventListener('open-finance', onFinance)
      window.removeEventListener('close-finance', offFinance)
    }
  }, [])

  /* Re-render once tenancy seat data arrives so the Admin link appears */
  useEffect(() => {
    if (isSeatLoaded()) return
    const id = setInterval(() => {
      if (isSeatLoaded()) { setSeatTick(t => t + 1); clearInterval(id) }
    }, 100)
    return () => clearInterval(id)
  }, [])

  /* Push page content + persist open state */
  useEffect(() => {
    document.body.classList.toggle('aj-nav-open', open)
    try { localStorage.setItem(NAV_OPEN_KEY, String(open)) } catch { /* ignore */ }
    return () => document.body.classList.remove('aj-nav-open')
  }, [open])

  /* Add scroll shadow to the chrome bar */
  useEffect(() => {
    const chromeEl = document.getElementById('aj-sc-chrome')
    if (!chromeEl) return
    function onScroll() {
      chromeEl.classList.toggle('aj-chrome-scrolled', window.scrollY > 0)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  function close() { setOpen(false) }

  /* ── Theme-aware colors ──────────────────────────────────────────────── */
  const bg         = dark ? '#0d1e24' : '#f0f4f6'
  const border     = dark ? 'rgba(255,255,255,0.07)' : 'rgba(7,39,45,0.10)'
  const textPrim   = dark ? 'rgba(207,226,232,0.90)' : '#07272D'
  const textMuted  = dark ? 'rgba(91,132,148,1)'     : 'rgba(7,39,45,0.38)'
  const textNav    = dark ? 'rgba(180,210,220,0.70)' : 'rgba(7,39,45,0.65)'
  const textNavAct = dark ? 'rgba(207,226,232,0.95)' : '#07272D'
  const navActBg   = dark ? 'rgba(255,255,255,0.07)' : 'rgba(7,39,45,0.07)'
  const sectionLbl = dark ? 'rgba(91,132,148,0.75)'  : 'rgba(7,39,45,0.35)'
  const soonBorder = dark ? 'rgba(255,255,255,0.12)' : 'rgba(7,39,45,0.15)'
  const soonText   = dark ? 'rgba(91,132,148,0.70)'  : 'rgba(7,39,45,0.30)'
  const divider    = dark ? 'rgba(255,255,255,0.07)' : 'rgba(7,39,45,0.08)'
  const closeColor = dark ? 'rgba(180,210,220,0.30)' : 'rgba(7,39,45,0.30)'
  const profileN   = dark ? 'rgba(207,226,232,0.82)' : 'rgba(7,39,45,0.80)'
  const profileE   = dark ? 'rgba(91,132,148,0.70)'  : 'rgba(7,39,45,0.40)'

  const S = {
    panel: {
      position: 'fixed', top: 0, left: 0, bottom: 0, width: NAV_W,
      background: bg,
      borderRight: `0.5px solid ${border}`,
      zIndex: 500,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      transform: open ? 'translateX(0)' : `translateX(-${NAV_W}px)`,
      transition: 'transform 0.22s ease',
    },
    header: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 16px',
      borderBottom: `0.5px solid ${divider}`,
      flexShrink: 0,
    },
    logoMark: {
      width: 26, height: 26, background: '#1D9E75', borderRadius: 6,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: '"Montserrat", sans-serif', fontWeight: 700, fontSize: 11,
      letterSpacing: '0.06em', color: '#ffffff', flexShrink: 0,
    },
    logoText: {
      fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: 11,
      letterSpacing: '0.10em', textTransform: 'uppercase', color: textPrim, lineHeight: 1.2,
    },
    closeBtn: {
      background: 'none', border: 'none', cursor: 'pointer',
      color: closeColor, padding: 4, borderRadius: 4,
      display: 'flex', alignItems: 'center', transition: 'color 0.1s',
    },
    navBody: { flex: 1, overflowY: 'auto', padding: '10px 8px' },
    sectionLabel: {
      fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: 9,
      letterSpacing: '0.14em', textTransform: 'uppercase',
      color: sectionLbl, padding: '10px 8px 4px',
    },
    navItem: {
      display: 'flex', alignItems: 'center', gap: 9, padding: '7px 8px',
      borderRadius: 6, cursor: 'pointer',
      fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 400,
      color: textNav, background: 'none', border: 'none',
      width: '100%', textAlign: 'left',
      transition: 'background 0.1s, color 0.1s', textDecoration: 'none',
    },
    navItemActive: { background: navActBg, color: textNavAct },
    navItemDisabled: { opacity: 0.40, cursor: 'default', pointerEvents: 'none' },
    comingSoon: {
      fontFamily: '"DM Sans", sans-serif', fontSize: 10,
      color: soonText, padding: '2px 5px', borderRadius: 3,
      border: `0.5px solid ${soonBorder}`, marginLeft: 'auto', flexShrink: 0,
    },
    footer: { borderTop: `0.5px solid ${divider}`, padding: '8px 8px', flexShrink: 0 },
    profile: {
      display: 'flex', alignItems: 'center', gap: 9,
      padding: '8px 8px 10px',
      borderBottom: `0.5px solid ${divider}`,
      marginBottom: 4,
    },
    avatar: {
      width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#1a6fa8',
      fontFamily: '"DM Sans", sans-serif', fontSize: 11, fontWeight: 600,
      color: '#ffffff', overflow: 'hidden',
    },
    profileName:  { fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 500, color: profileN, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    profileEmail: { fontFamily: '"DM Sans", sans-serif', fontSize: 10, color: profileE, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    // Compact-viewport only (2026-09-04, the workspace mobile pass) — see the matching
    // @media (max-width: 760px) rule in outline.css, which is what actually shows this and
    // is what beats this inline `display:'none'` there. Hidden here so mounting it whenever
    // the tray is open has zero effect above the breakpoint (nothing to click, nothing drawn).
    backdrop: {
      position: 'fixed', inset: 0, zIndex: 499, background: 'rgba(0,0,0,0.35)', display: 'none',
    },
  }

  return (
    <>
    {open && <div className="aj-sitenav-backdrop" style={S.backdrop} onClick={close} />}
    <div style={S.panel}>
      {/* Header */}
      <div style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={S.logoMark}>AJ</div>
          <div>
            <div style={S.logoText}>Alder James</div>
            <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 10, color: textMuted, marginTop: 1 }}>
              Studio Platform
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <button
            style={S.closeBtn}
            onClick={toggleTheme}
            title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {dark ? <IconSun /> : <IconMoon />}
          </button>
          <button style={S.closeBtn} onClick={close} title="Close">
            <IconClose />
          </button>
        </div>
      </div>

      {/* Nav body */}
      <div style={S.navBody}>
        <div style={S.sectionLabel}>Workspace</div>

        <button
          style={{ ...S.navItem, ...((!billingActive && !trackerActive && !pulseActive && !teamActive && !crmActive && !studioActive && !permittingActive && !constructionActive && !financeActive) ? S.navItemActive : {}) }}
          onClick={e => {
            if (e.ctrlKey || e.metaKey) { window.open(wsUrl({ ws: 'projects' }), '_blank'); return }
            ctx.closeAllWorkspaces()
          }}
          title="Projects"
        >
          <IconGrid />
          Projects
        </button>

        {/* Studio — firm-operations hub: Calendar (everyone) / Org Chart, Board, Handbook,
            Mission & Values (admin-only, hidden entirely from the tab bar for anyone else —
            Studio Calendar moved in here from its own standalone workspace, 2026-08) */}
        <button
          style={{ ...S.navItem, ...(studioActive ? S.navItemActive : {}) }}
          title="Studio"
          onClick={e => {
            if (e.ctrlKey || e.metaKey) { window.open(wsUrl({ ws: 'studio' }), '_blank'); return }
            ctx.closeAllWorkspaces(); window.dispatchEvent(new CustomEvent('open-studio'))
          }}
        >
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
            <circle cx="8" cy="4.5" r="2.5" />
            <path d="M2 14c0-3 2.7-5 6-5s6 2 6 5" strokeLinecap="round" />
          </svg>
          Studio
        </button>

        {/* Roadmap & Bugs — Feature Roadmap + Bug Tracker workspace (manager and up — roadmap module) */}
        {canRoadmap && (
          <button
            style={{ ...S.navItem, ...(trackerActive ? S.navItemActive : {}) }}
            title="Roadmap & Bugs"
            onClick={e => {
              if (e.ctrlKey || e.metaKey) { window.open(wsUrl({ ws: 'tracker' }), '_blank'); return }
              ctx.closeAllWorkspaces(); window.dispatchEvent(new CustomEvent('open-tracker'))
            }}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
              <rect x="2" y="2" width="12" height="12" rx="2" />
              <path d="M5 6h6M5 9h6M5 12h3" strokeLinecap="round" />
            </svg>
            Roadmap &amp; Bugs
          </button>
        )}

        {/* Permitting Hub — Zones & Jurisdictions library + cross-project Permits board
            (manager and up — permitting module). Renamed from "Permitting" 2026-10. */}
        {canPermitting && (
          <button
            style={{ ...S.navItem, ...(permittingActive ? S.navItemActive : {}) }}
            title="Permitting Hub"
            onClick={e => {
              if (e.ctrlKey || e.metaKey) { window.open(wsUrl({ ws: 'permitting' }), '_blank'); return }
              ctx.closeAllWorkspaces(); window.dispatchEvent(new CustomEvent('open-permitting'))
            }}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
              <path d="M8 1.5 2.5 4v4c0 4 2.4 6.7 5.5 7.5 3.1-0.8 5.5-3.5 5.5-7.5V4L8 1.5z" strokeLinejoin="round" />
              <path d="M5.8 8.2l1.6 1.6 2.9-3.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Permitting Hub
          </button>
        )}

        {/* Construction Hub — the companion to Permitting Hub: open construction items, contracts
            and portals, contractors, and what is waiting, across every project. Everyone signed in
            (Matt, 2026-10) — no module gate. See docs/CONSTRUCTION_HUB.md. */}
        <button
          style={{ ...S.navItem, ...(constructionActive ? S.navItemActive : {}) }}
          title="Construction Hub"
          onClick={e => {
            if (e.ctrlKey || e.metaKey) { window.open(wsUrl({ ws: 'construction' }), '_blank'); return }
            ctx.closeAllWorkspaces(); window.dispatchEvent(new CustomEvent('open-construction-hub'))
          }}
        >
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
            <path d="M2 13.5h12" strokeLinecap="round" />
            <path d="M3.5 13.5V8.5L8 5l4.5 3.5v5" strokeLinejoin="round" />
            <path d="M6.5 13.5v-3h3v3" strokeLinejoin="round" />
            <path d="M8 5V2.5h3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Construction Hub
        </button>

        {/* Finances (Matt, 2026-11-03): Bookkeeping and Billing are its two tabs (FinancesTabs.jsx),
            each still gated by its own module; the item shows to anyone holding either. */}
        {(canFinance || canBilling) && (
          <button
            style={{ ...S.navItem, ...((financeActive || billingActive) ? S.navItemActive : {}) }}
            title="Finances"
            onClick={e => {
              if (e.ctrlKey || e.metaKey) { window.open(wsUrl(canFinance ? { ws: 'finance' } : { ws: 'billing', wsTool: 'readiness' }), '_blank'); return }
              openFinances()
            }}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
              <rect x="2" y="3" width="12" height="10" rx="1.5" strokeLinejoin="round" />
              <path d="M2 6.5h12" strokeLinecap="round" />
              <path d="M5 9.5h3" strokeLinecap="round" />
            </svg>
            Finances
          </button>
        )}

        {/* Flight Board — the firm's operating cockpit: decisions, workload + risk, and the
            scheduler (manager and up). Folds in the former "Schedule Pulse"; the scheduler
            run-control is reachable from inside it. Icon is the same plane silhouette used for
            milestones on the Flight Board's own hill chart (FlightTracker.jsx's aj-ft-plane
            symbol) — same path data, redrawn inline (filled, not the outline style the other
            nav icons use) since that symbol only exists in the DOM while the board is open. */}
        {canWorkload && (
          <button
            style={{ ...S.navItem, ...(teamActive ? S.navItemActive : {}) }}
            title="Flight Board"
            onClick={e => {
              if (e.ctrlKey || e.metaKey) { window.open(wsUrl({ ws: 'team' }), '_blank'); return }
              ctx.closeAllWorkspaces(); window.dispatchEvent(new CustomEvent('open-team'))
            }}
          >
            <svg width="15" height="11" viewBox="8 23 82 60" fill="currentColor">
              <path fillRule="evenodd" d="m17.934 62.523-7.6484-8.418c-0.23438-0.25781-0.33203-0.57812-0.27734-0.92578 0.054688-0.34375 0.24219-0.62109 0.54688-0.79688l4.3398-2.5039c0.32031-0.1875 0.67969-0.20312 1.0195-0.050781l10.188 4.6094c0.33984 0.15625 0.70313 0.13672 1.0273-0.054688l15.418-9.0586c0.28906-0.16797 0.46094-0.44141 0.49219-0.77344s-0.09375-0.63281-0.34766-0.85156l-12.906-11.074c-0.28516-0.24219-0.42188-0.57812-0.38672-0.95312 0.035156-0.375 0.23047-0.67969 0.55469-0.86719l6.0195-3.4727c0.29297-0.16797 0.60938-0.19922 0.92969-0.085937l19.688 6.9141 2.6641 0.9375c0.28906 0.10156 0.57812 0.074219 0.83984-0.082031l15.754-9.2578c4.6016-2.6641 9.1523-2.5469 13.652 0.35156 0.51562 0.33203 0.66797 1.0156 0.33984 1.5352-2.3516 3.75-9.3945 8.8984-21.117 15.445-0.30078 0.16797-0.49219 0.43359-0.55469 0.76953l-5.2461 27.906c-0.0625 0.33203-0.24609 0.59375-0.53906 0.76172l-6.0195 3.4766c-0.32422 0.1875-0.6875 0.20312-1.0273 0.046875-0.33984-0.15625-0.5625-0.44141-0.63281-0.80859l-3.7227-19.82c-0.070313-0.36719-0.29297-0.65234-0.63281-0.80859s-0.70312-0.14062-1.0273 0.046875l-16.617 9.5938c-0.023437 0.015625-0.046875 0.027344-0.070313 0.039063-6.5859 3.1914-11.465 2.6328-14.625-1.6758-0.027343-0.035157-0.046874-0.0625-0.074218-0.09375z" />
            </svg>
            Flight Board
          </button>
        )}

        {/* CRM — Leads / Opportunities / Proposals + Fee Models workspace (crm module) */}
        {canCrm && (
          <button
            style={{ ...S.navItem, ...(crmActive ? S.navItemActive : {}) }}
            title="CRM"
            onClick={e => {
              if (e.ctrlKey || e.metaKey) { window.open(wsUrl({ ws: 'crm', wsTool: 'leads' }), '_blank'); return }
              ctx.closeAllWorkspaces(); window.dispatchEvent(new CustomEvent('open-crm'))
            }}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
              <circle cx="6" cy="5" r="3" stroke="currentColor" strokeWidth="1.3"/>
              <path d="M1 14c0-2.76 2.24-5 5-5s5 2.24 5 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              <line x1="12" y1="8" x2="15" y2="8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              <line x1="12" y1="11" x2="15" y2="11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            CRM
          </button>
        )}

        {/* ── Utilities — modal tools, nav stays open ── */}
        <div style={{ ...S.sectionLabel, marginTop: 12 }}>Utilities</div>

        {/* (My Work retired 2026-09-02 — a person's flights are pinned above the outline instead;
            see MyFlightsStrip.jsx and docs/FLOW_RECONCILIATION_PLAN.md decision 9.) */}

        {/* Contacts — opens directory popup, keeps nav open */}
        <button
          style={S.navItem}
          title="Contacts"
          onClick={() => window.dispatchEvent(new CustomEvent('open-contacts-panel'))}
        >
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
            <circle cx="6" cy="5" r="2.6"/>
            <path d="M1.5 14c0-2.5 2-4.5 4.5-4.5s4.5 2 4.5 4.5" strokeLinecap="round"/>
            <line x1="12.5" y1="6" x2="12.5" y2="11" strokeLinecap="round"/>
            <line x1="10" y1="8.5" x2="15" y2="8.5" strokeLinecap="round"/>
          </svg>
          Contacts
        </button>

        {/* AlderSKETCH — the same /sketch the slash command opens, reachable by touch on a tablet
            with no keyboard (Matt, 2026-11-01). Everyone signed in. */}
        <button
          style={S.navItem}
          title="AlderSKETCH"
          onClick={e => {
            if (e.ctrlKey || e.metaKey) { window.open('/sketch', '_blank'); return }
            window.location.assign('/sketch')
          }}
        >
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
            <path d="M10.5 2.5l3 3-8 8H2.5v-3z" strokeLinejoin="round"/>
            <line x1="9" y1="4" x2="12" y2="7" strokeLinecap="round"/>
          </svg>
          AlderSKETCH
        </button>

        {/* Commands — the "/" command bar, which otherwise needs a keyboard to open (Matt,
            2026-11-01, docs/MOBILE_UX_AUDIT.md). On a phone the tray overlays the page, so it
            closes first; on a wide screen it stays open like Contacts. */}
        <button
          style={S.navItem}
          title="Commands (/)"
          onClick={() => {
            if (window.innerWidth <= 760) close()
            window.dispatchEvent(new CustomEvent('open-command-bar'))
          }}
        >
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
            <rect x="1.5" y="2.5" width="13" height="11" rx="1.5"/>
            <line x1="9.5" y1="5" x2="6.5" y2="11" strokeLinecap="round"/>
          </svg>
          Commands
        </button>

        {/* Expenses — file one, approve one, record one. No module gate on the BUTTON: filing an
            expense is everyone's job. The approve and record tabs appear only when the server
            says so, and it decides again on every call. */}
        <button
          style={S.navItem}
          title="Expenses"
          onClick={() => window.dispatchEvent(new CustomEvent('open-expenses-panel'))}
        >
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
            <path d="M3 2h7l3 3v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" strokeLinejoin="round"/>
            <path d="M9.5 2v3.5H13" strokeLinejoin="round"/>
            <line x1="4.5" y1="9" x2="10" y2="9" strokeLinecap="round"/>
            <line x1="4.5" y1="11.5" x2="8" y2="11.5" strokeLinecap="round"/>
          </svg>
          Expenses
        </button>

        {/* Time Card — all staff */}
        <button
          style={S.navItem}
          title="Time Card"
          onClick={() => window.dispatchEvent(new CustomEvent('open-timecard'))}
        >
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
            <circle cx="8" cy="8" r="6.5"/>
            <path d="M8 4.5V8l2.5 1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Time Card
        </button>

        {/* Time Review — managers (timecard-admin module) */}
        {isTimeReviewer && (
          <button
            style={S.navItem}
            title="Time Review"
            onClick={() => window.dispatchEvent(new CustomEvent('open-timecard-review'))}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
              <rect x="2" y="2.5" width="12" height="11" rx="1.5"/>
              <path d="M4.5 7l2 2 3.5-4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Time Review
          </button>
        )}

        {/* Expense Review — the same reviewers, their own window since 2026-10-28 */}
        {isTimeReviewer && (
          <button
            style={S.navItem}
            title="Expense Review"
            onClick={() => window.dispatchEvent(new CustomEvent('open-expense-review'))}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
              <rect x="2.5" y="1.5" width="11" height="13" rx="1.5"/>
              <path d="M5 5h6M5 8h6M5 11h3.5" strokeLinecap="round"/>
            </svg>
            Expense Review
          </button>
        )}

        {/* Time & Expense Records — the per-project record browser (docs/TIME_RECORDS.md). Same
            audience as Time Review: they can already edit and post time, this just organizes it by
            project instead of by person. */}
        {isTimeReviewer && (
          <button
            style={S.navItem}
            title="Time &amp; Expense Records"
            onClick={() => window.dispatchEvent(new CustomEvent('open-time-records'))}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
              <path d="M2.5 3.5h11M2.5 8h11M2.5 12.5h7" strokeLinecap="round"/>
              <circle cx="12.5" cy="12.5" r="2.2"/>
            </svg>
            Time Records
          </button>
        )}

        {isAdmin && (
          <button
            style={S.navItem}
            title="Admin"
            onClick={() => window.dispatchEvent(new CustomEvent('open-admin-modal'))}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
              <rect x="1" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
              <rect x="9" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
              <rect x="1" y="9" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
              <line x1="12" y1="9" x2="12" y2="15" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              <line x1="9" y1="12" x2="15" y2="12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
            </svg>
            Admin
          </button>
        )}

        {canSpecLibrary && (
          <button
            style={S.navItem}
            title="AlderSPEC Library — the firm master spec library"
            onClick={() => window.dispatchEvent(new CustomEvent('open-spec-library'))}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
              <path d="M2 3.5A1.5 1.5 0 0 1 3.5 2H7v12H3.5A1.5 1.5 0 0 1 2 12.5v-9Z" stroke="currentColor" strokeWidth="1.3"/>
              <path d="M14 3.5A1.5 1.5 0 0 0 12.5 2H9v12h3.5a1.5 1.5 0 0 0 1.5-1.5v-9Z" stroke="currentColor" strokeWidth="1.3"/>
            </svg>
            AlderSPEC Library
          </button>
        )}

        {canScheduleLibrary && (
          <button
            style={S.navItem}
            title="Master Schedule Library"
            onClick={() => window.dispatchEvent(new CustomEvent('open-schedule-library'))}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
              <rect x="2" y="2.5" width="12" height="11" rx="1.5"/>
              <line x1="2" y1="6" x2="14" y2="6"/>
              <line x1="6.5" y1="6" x2="6.5" y2="13.5"/>
            </svg>
            Schedule Library
          </button>
        )}

        {canTemplates && (
          <button
            style={{ ...S.navItem, ...(templatesActive ? S.navItemActive : {}) }}
            title="Checklist Templates"
            onClick={() => {
              ctx.closeAllWorkspaces()
              window.dispatchEvent(new CustomEvent('open-templates-workspace'))
            }}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
              <rect x="2" y="2" width="12" height="12" rx="1.5"/>
              <line x1="5" y1="6" x2="11" y2="6" strokeLinecap="round"/>
              <line x1="5" y1="9" x2="9" y2="9" strokeLinecap="round"/>
              <line x1="5" y1="12" x2="7" y2="12" strokeLinecap="round"/>
            </svg>
            Templates
          </button>
        )}

      </div>

      {/* Footer */}
      <div style={S.footer}>
        {/* User profile */}
        {user && (
          <div style={S.profile}>
            <div style={S.avatar}>
              {user.imageUrl ? (
                <img src={user.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                userInitials(user)
              )}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={S.profileName}>{user.fullName || user.firstName || 'Account'}</div>
              <div style={S.profileEmail}>{user.primaryEmailAddress?.emailAddress || ''}</div>
            </div>
          </div>
        )}

        <button style={S.navItem} onClick={() => window.dispatchEvent(new CustomEvent('open-user-settings-modal'))} title="My Signature">
          <IconSettings />
          My Signature
        </button>
        <button style={S.navItem} onClick={() => signOut()} title="Sign out">
          <IconSignOut />
          Sign out
        </button>
      </div>
    </div>
    </>
  )
}
